import React, { useState, useEffect, useMemo } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Checkbox from "aldoo-ra/Checkbox"
import Value from "aldoo-ra/Value"
import { showNotification } from "aldoo-ra/Notification"
import {
  MergeTickets as MergeTicketsAPI,
  GetTickets,
  GetStatistics,
} from "./api"
import { ArrowPathIcon, ChevronRightIcon, InformationCircleIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import { formatDate, getRelativeTimeString } from "./time-format"
import ConfirmMerge from "./ConfirmMerge"

const MergeTickets = ({
  isOpen,
  onClose,
  initialTicketId,
  initialTicketLabel,
  preSelectedTickets = [], // New prop for pre-selected tickets
}) => {
  const [loading, setLoading] = useState(false)
  const [selectedTickets, setSelectedTickets] = useState([])
  const [primaryTicketId, setPrimaryTicketId] = useState(null)
  const [step, setStep] = useState(1) // 1 = select tickets, 2 = confirm merge
  const [, setStatistics] = Value("cs-statistics", null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [dataSource, setDataSource] = useState([])

  // Load initial ticket when component mounts
  useEffect(() => {
    if (isOpen) {
      // If we have pre-selected tickets, use those
      if (preSelectedTickets.length > 0) {
        setSelectedTickets(preSelectedTickets)
        // Set the first ticket as primary by default
        setPrimaryTicketId(preSelectedTickets[0].ticketID)
      } else if (initialTicketId) {
        // Otherwise, use the initial ticket
        setSelectedTickets([
          {
            ticketID: initialTicketId,
            label: initialTicketLabel || initialTicketId,
            summary: initialTicketLabel
              ? initialTicketLabel.replace(initialTicketId, "").trim()
              : "",
          },
        ])
        // Also set it as the primary ticket by default
        setPrimaryTicketId(initialTicketId)
      }

      // Refresh the data when dialog opens
      setRefreshKey((prev) => prev + 1)
    }
  }, [initialTicketId, initialTicketLabel, isOpen, preSelectedTickets])

  // Get request fields for API
  const getRequestFields = () => {
    // Get all ticket IDs that should be excluded (already selected)
    const excludedTicketIds = selectedTickets.map(t => t.ticketID)
    if (initialTicketId && !excludedTicketIds.includes(initialTicketId)) {
      excludedTicketIds.push(initialTicketId)
    }
    
    return {
      match: {
        status: { $in: ["open", "pending"] },
        isMerged: { $ne: true },
        ticketID: { $nin: excludedTicketIds }, // Exclude all selected tickets
        isSpam: { $ne: true },
      },
    }
  }

  // Handle data loaded from DataTable
  const handleDataLoaded = (loadedData) => {
    setDataSource(loadedData || [])
    return loadedData
  }

  // Handle ticket selection
  const handleSelectItem = (ticket, isSelected) => {
    if (isSelected) {
      // If this is the first ticket added, set it as primary by default
      if (selectedTickets.length === 0) {
        setPrimaryTicketId(ticket.ticketID)
      }

      // Add the ticket to selection if not already there
      if (!selectedTickets.some((t) => t.ticketID === ticket.ticketID)) {
        const newTicket = {
          ticketID: ticket.ticketID,
          label: `${ticket.ticketID} - ${ticket.summary}`,
          summary: ticket.summary,
          from: ticket.author,
          created: ticket.timestamp,
        }
        setSelectedTickets((prev) => [...prev, newTicket])
      }
    } else {
      // If removing the primary ticket, reset primaryTicketId
      if (ticket.ticketID === primaryTicketId) {
        const remainingTickets = selectedTickets.filter(
          (t) => t.ticketID !== ticket.ticketID
        )
        setPrimaryTicketId(
          remainingTickets.length > 0 ? remainingTickets[0].ticketID : null
        )
      }

      // Remove the ticket from selection
      setSelectedTickets((prev) =>
        prev.filter((t) => t.ticketID !== ticket.ticketID)
      )
    }
  }

  // Handle initial ticket removal
  const handleRemoveInitialTicket = () => {
    // If this is the primary ticket, reset primaryTicketId
    if (initialTicketId === primaryTicketId) {
      const remainingTickets = selectedTickets.filter(
        (t) => t.ticketID !== initialTicketId
      )
      setPrimaryTicketId(
        remainingTickets.length > 0 ? remainingTickets[0].ticketID : null
      )
    }

    // Remove the initial ticket from selection
    setSelectedTickets((prev) =>
      prev.filter((t) => t.ticketID !== initialTicketId)
    )
  }

  // Set a ticket as primary
  const handleSetPrimary = (ticketId) => {
    setPrimaryTicketId(ticketId)
  }

  // Go to confirm step
  const handleContinue = () => {
    if (selectedTickets.length < 2) {
      showNotification({
        message: "Please select at least two tickets to merge",
        className: "bg-admin_error text-white",
      })
      return
    }

    if (!primaryTicketId) {
      showNotification({
        message: "Please select a primary ticket",
        className: "bg-admin_error text-white",
      })
      return
    }

    // Move to confirmation step
    setStep(2)
  }

  // Handle the actual merge operation
  const handleMerge = async (notes) => {
    setLoading(true)

    try {
      const secondary = selectedTickets
        .filter((ticket) => ticket.ticketID !== primaryTicketId)
        .map((ticket) => ticket.ticketID)

      const response = await MergeTicketsAPI({
        primaryTicketId,
        secondaryTicketIds: secondary,
        notes: notes,
      })

      if (response.result) {
        showNotification({
          message: "Tickets merged successfully",
          className: "bg-admin_primary text-admin_text_inv",
        })

        // Refresh statistics
        try {
          const statsResponse = await GetStatistics()
          if (statsResponse.result) {
            setStatistics(statsResponse.data)
          }
        } catch (statsError) {
          console.error("Failed to refresh statistics:", statsError)
        }

        // Close the modal and refresh the parent view
        onClose && onClose(true) // Pass true to indicate successful merge
      } else {
        showNotification({
          message: response.message || "Failed to merge tickets",
          className: "bg-admin_error text-white",
        })
      }
    } catch (error) {
      showNotification({
        message: "An error occurred while merging tickets",
        className: "bg-admin_error text-white",
      })
      console.error("Error merging tickets:", error)
    } finally {
      setLoading(false)
    }
  }

  // Handle cancel in confirmation step
  const handleBack = () => {
    setStep(1)
  }

  // Reset state when closing the modal
  const handleClose = () => {
    setSelectedTickets([])
    setPrimaryTicketId(null)
    setStep(1)
    onClose && onClose()
  }

  // Style overrides for the DataTable
  const styleOverrides = {
    searchInput: "w-full max-w-[300px] xl:max-w-[360px] mr-4",
    table: "w-full",
    container: "w-full",
    wrapper: "w-full",
    getRowStyle: (index, item) => {
      // Add selected state highlighting
      if (item && selectedTickets.some((t) => t.ticketID === item.ticketID)) {
        return "bg-admin_primary/10"
      }
    },
  }

  // Column definitions for the DataTable
  const columns = [
    {
      id: "select",
      headerClassName: "w-[60px]",
      render: (_, item) => (
        <Checkbox
          variant="admin"
          isOn={selectedTickets.some((t) => t.ticketID === item.ticketID)}
          onChange={(isSelected) => handleSelectItem(item, isSelected)}
        />
      ),
    },
    {
      id: "details",
      title: "Ticket Details",
      headerClassName: "w-[70%]",
      render: (_, item) => (
        <div
          className="flex flex-col cursor-pointer py-4"
          onClick={() => handleSelectItem(item, true)}
        >
          <Typography
            variant="admin_body"
            className="truncate max-w-md font-medium my-0"
          >
            {item.summary}
          </Typography>

          <div className="flex items-center text-admin_text_secondary mt-4">
            <Typography variant="admin_txt1" className="mr-2">
              #{item.ticketID}
            </Typography>

            <Typography
              variant="admin_txt1"
              className="text-admin_text_secondary/70"
            >
              {formatDate(item.timestamp)} (
              {getRelativeTimeString(item.timestamp)})
            </Typography>
          </div>
        </div>
      ),
    },
    {
      id: "author",
      title: "From",
      headerClassName: "w-[30%]",
      render: (_, item) => (
        <Typography variant="admin_body" className="truncate max-w-full my-0">
          {item.author}
        </Typography>
      ),
    },
  ]

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Merge Tickets"
      variant="admin_fullScreen"
      styleOverrides={{
        container: "relative w-full h-full bg-admin_canvas",
        content: "w-full h-full flex flex-col p-6",
      }}
    >
      {step === 1 ? (
        // Step 1: Select tickets - New fullscreen layout
        <div className="flex flex-col h-full">
          {/* Top-right duplicate buttons */}
          <div className="absolute top-5 right-[90px] flex justify-end mb-4">
            <Button
              variant="admin_secondary"
              onClick={handleClose}
              className="min-w-[120px] mr-3"
            >
              Cancel
            </Button>

            <Button
              variant="admin_primary"
              onClick={handleContinue}
              disabled={selectedTickets.length < 2 || !primaryTicketId}
              className="flex items-center pl-7"
            >
              Continue
              <ChevronRightIcon className="h-5 w-5 ml-1" />
            </Button>
          </div>

          <div className="flex-1 flex gap-6 mb-6">
            {/* Left Side - Ticket Search */}
            <div
              className="w-[60%] min-w-[510px] flex flex-col flex-shrink-0 bg-admin_paper border border-admin_bordered rounded-3xl p-6 overflow-hidden"
              style={{ minWidth: "480px" }}
            >
              <div className="relative flex items-center justify-between mb-4">
                <Typography 
                  component="h1"
                  variant="admin_h4"
                >
                  Available Tickets
                </Typography>

                <Button
                  variant="admin_secondary"
                  onClick={() => setRefreshKey((prev) => prev + 1)}
                  className="absolute right-4 top-[42px] flex items-center gap-2"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  Refresh
                </Button>
              </div>

              {/* Data Table - Fill remaining height */}
              <div className="flex-1 w-full">
                <DataTable
                  variant="admin_browser"
                  apiMethod={GetTickets}
                  requestFields={getRequestFields()}
                  searchField="summary author ticketID"
                  sort={{ timestamp: -1 }}
                  localUserPreferencesKey="admin.customerSupport.mergeTickets"
        renders={columns}
                  refreshKey={refreshKey}
                  preferredHeight="60vh"
                  itemHeight="h-[60px]"
                  noData={
                    <Typography variant="admin_body">
                      No available tickets found
                    </Typography>
                  }
                  styleOverrides={{
                    ...styleOverrides,
                  }}
                  onDataLoaded={handleDataLoaded}
                />
              </div>
            </div>

            {/* Right Side - Ticket Selection */}
            <div className="w-[40%] flex flex-col p-4">
              <Typography variant="admin_h4" className="mb-4">
                Selected Tickets
              </Typography>

              <div className="flex items-start gap-2">
                <InformationCircleIcon className="h-6 w-6 flex-shrink-0 text-admin_warning" />

                <Typography 
                  variant="admin_body_dim"
                  className="my-0"
                >
                  Conversations from the merged tickets will be added to the
                  primary ticket. The secondary tickets will be closed.
                </Typography>
              </div>

              <Typography variant="admin_body_dim" className="mb-2">
                {selectedTickets.length} tickets selected
              </Typography>

              {/* Selected tickets list - takes remaining height */}
              <div className="flex-1 overflow-y-auto">
                <div className="space-y-3 min-h-[400px]">
                  {selectedTickets.length > 0 ? (
                    selectedTickets.map((ticket) => (
                      <div
                        key={ticket.ticketID}
                        className={`border ${
                          ticket.ticketID === primaryTicketId
                            ? "bg-admin_paper border-admin_warning/50"
                            : "bg-admin_paper/70 hover:bg-admin_paper border-admin_paper hover:border-admin_warning/30"
                        } rounded-md p-3 relative cursor-pointer transition-colors`}
                        onClick={() => handleSetPrimary(ticket.ticketID)}
                      >
                        <div className="flex items-start p-4">
                          <div className="flex-1 min-w-0">
                            <Typography
                              variant="admin_body"
                              className="text-[18px] xl:text-[20px] font-medium truncate my-0"
                            >
                              {ticket.summary}
                            </Typography>

                            <Typography 
                              variant="admin_body_dim"
                              className="mb-0">
                              #{ticket.ticketID}
                              {ticket.from && (
                                <span className="truncate ml-2">
                                  From: {ticket.from}
                                </span>
                              )}
                            </Typography>
                          </div>

                          {ticket.ticketID === primaryTicketId ? (
                            <div className="absolute top-2 right-2 bg-admin_warning text-sm text-admin_text_inv rounded-sm px-2 py-0.5">
                              Primary
                            </div>
                          ) : (
                            <button
                              className="absolute top-2 right-2 text-admin_text_secondary hover:text-admin_error p-1 rounded-full hover:bg-admin_canvas transition-colors"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleSelectItem(ticket, false)
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center justify-center h-full min-h-[200px]">
                      <Typography
                        variant="admin_body"
                        className="text-admin_text_secondary"
                      >
                        No tickets selected. Select tickets from the left panel.
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer with action buttons - added mb-4 and pb-8 for more space */}
          <div className="flex justify-end pb-8 mb-4 mr-[70px]">
            <Button
              variant="admin_secondary"
              onClick={handleClose}
              className="min-w-[120px] mr-3"
            >
              Cancel
            </Button>

            <Button
              variant="admin_primary"
              onClick={handleContinue}
              disabled={selectedTickets.length < 2 || !primaryTicketId}
              className="pl-7"
            >
              Continue
              <ChevronRightIcon className="h-5 w-5 ml-1" />
            </Button>
          </div>
        </div>
      ) : (
        // Step 2: Confirm merge - using ConfirmMerge component with full width
        <div className="w-full h-full flex flex-col">
          <ConfirmMerge
            selectedTickets={selectedTickets}
            primaryTicketId={primaryTicketId}
            onConfirm={handleMerge}
            onBack={handleBack}
            loading={loading}
          />
        </div>
      )}
    </Modal>
  )
}

export default MergeTickets
